import { Link } from "react-router-dom";
import { ArrowRight, Cog, CheckCircle2 } from "lucide-react";
import SEO from "../components/SEO";
import { BUSINESS, IMAGES } from "../lib/site";

const ENGINES = [
  {
    name: "Ford 1.0 EcoBoost",
    price: "£980",
    service: "Ford 1.0 EcoBoost Wet Belt",
    desc: "Complete wet belt replacement for the Ford 1.0 EcoBoost engine, using genuine Ford parts.",
    testid: "ecoboost",
  },
  {
    name: "Ford Transit 2.0",
    price: "£1,050",
    service: "Ford Transit 2.0 Wet Belt",
    desc: "Wet belt replacement for the Ford Transit 2.0 engine, using genuine Ford parts.",
    testid: "transit",
  },
];

const POINTS = [
  "The wet belt (belt-in-oil timing belt) runs inside the engine, lubricated by engine oil",
  "Over time the belt material degrades and must be replaced at the correct interval",
  "Using the correct genuine Ford parts and the correct oil specification matters",
  "If you are unsure of your belt history, ask us to advise before problems develop",
];

export default function WetBeltsPage() {
  return (
    <>
      <SEO
        title="Ford Wet Belt Replacement Adlington | 1.0 EcoBoost £980, Transit 2.0 £1,050"
        description="Specialist Ford wet belt replacement in Lancashire. Ford 1.0 EcoBoost wet belt replacement £980 and Ford Transit 2.0 wet belt replacement £1,050 using genuine Ford parts. Based in Adlington near Chorley. Call 01257 786 540."
        path="/ford-wet-belt-replacement"
      />

      <section className="relative bg-navy overflow-hidden">
        <div className="absolute inset-0 eng-grid" />
        <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-brand/10 blur-3xl" />
        <div className="container-pr7 relative py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow-light mb-3">Ford Wet Belt Replacement</p>
            <h1 className="font-heading font-black text-white text-4xl sm:text-5xl tracking-tight leading-tight">
              Specialist Ford <span className="text-sky">wet belt replacement</span>
            </h1>
            <p className="text-pale/75 text-base sm:text-lg leading-relaxed mt-5 max-w-xl">
              PR7 MOT Centre carries out wet belt replacement on Ford 1.0 EcoBoost and Ford Transit 2.0 engines using genuine Ford parts, from our workshop in Adlington, Lancashire.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-7">
              <Link to="/booking?service=Ford 1.0 EcoBoost Wet Belt" data-testid="wetbelt-enquire-btn" className="btn-primary">ENQUIRE ABOUT WET BELT REPLACEMENT <ArrowRight size={17} /></Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 border border-sky/20 rounded-lg translate-x-3 translate-y-3" />
              <img src={IMAGES.wetBelt} alt="Ford engine wet belt and timing components close-up" className="relative rounded-lg object-cover w-full h-72 lg:h-96 shadow-2xl" loading="eager" />
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-pr7">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ENGINES.map((e) => (
              <div key={e.name} data-testid={`wetbelt-card-${e.testid}`} className="relative bg-pale border border-brand/25 rounded-lg overflow-hidden">
                <div className="h-1.5 bg-gradient-to-r from-brand to-sky" />
                <div className="p-8">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="eyebrow mb-2">Genuine Ford Parts</p>
                      <h2 className="font-heading font-extrabold text-ink text-2xl tracking-tight">{e.name}</h2>
                    </div>
                    <Cog className="text-brand shrink-0" size={34} strokeWidth={1.5} />
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed mt-4">{e.desc}</p>
                  <div className="flex items-end justify-between mt-7 pt-6 border-t border-brand/15">
                    <p className="font-mono font-semibold text-brand text-3xl">{e.price}</p>
                    <Link to={`/booking?service=${encodeURIComponent(e.service)}`} data-testid={`wetbelt-book-${e.testid}`} className="btn-primary !px-5 !py-2.5 text-sm">
                      ENQUIRE NOW <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-16 items-center">
            <div>
              <h2 className="font-heading font-extrabold text-ink text-2xl sm:text-3xl tracking-tight">Why wet belt maintenance matters</h2>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Ford's 1.0 EcoBoost and 2.0 EcoBlue engines use a timing belt that runs inside the engine in oil - commonly known as a wet belt. Like any timing belt, it is a service item with a replacement interval, and correct maintenance is essential to keep the engine running as it should.
              </p>
              <ul className="mt-6 space-y-3">
                {POINTS.map((p) => (
                  <li key={p} className="flex gap-3 text-sm text-muted-foreground leading-relaxed">
                    <CheckCircle2 size={17} className="text-brand shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-6">
                If your EcoBoost or Transit is approaching its belt interval, or you have no record of the belt being replaced, contact us to discuss the work. We confirm exactly what is included before any job is booked in.
              </p>
            </div>
            <div>
              <img src={IMAGES.engine} alt="Technician working on a Ford engine timing assembly" className="rounded-lg object-cover w-full h-80 lg:h-[420px] shadow-xl" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-navy py-14 relative overflow-hidden">
        <div className="absolute inset-0 eng-grid" />
        <div className="container-pr7 relative text-center">
          <h2 className="font-heading font-extrabold text-white text-2xl sm:text-3xl tracking-tight">Ford wet belt replacement in Lancashire</h2>
          <p className="text-pale/65 text-sm leading-relaxed mt-4 max-w-3xl mx-auto">
            Based in Adlington near Chorley, we carry out Ford EcoBoost wet belt replacement and Ford Transit 2.0 wet belt replacement for customers across Lancashire. Enquire online or call{" "}
            <a href={BUSINESS.phoneHref} className="text-sky font-semibold">{BUSINESS.phone}</a>.
          </p>
          <Link to="/booking?service=Ford 1.0 EcoBoost Wet Belt" data-testid="wetbelt-bottom-cta" className="btn-primary mt-7 inline-flex">ENQUIRE NOW <ArrowRight size={16} /></Link>
        </div>
      </section>
    </>
  );
}
